import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { RecommendedAction } from '@/versions/v1/schema/case';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { formatAffectsLine } from '@/versions/v1/modules/overview/CaseAffects.transform';
import { longTrend } from '@/versions/v1/shared/SpcEngine';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import type { CapacityCircuitId } from '@/versions/v1/schema/capacity';
import { bm01BearingTrend, bm01BearingTrendPhrase } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { capacityPlantState } from '@/versions/v1/modules/capacity/Scene.transform';
import { CEILING_LABEL_BY_CIRCUIT } from '@/versions/v1/modules/planning/Scene.transform';
import { evaluateCapacity } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { EXTRA_ROLE_TABLES, ROLE_TABLES } from '@/versions/v1/shared/bunker-hill-seed';
import { MAINTAINABLE_ITEMS } from './strategy.seed';
import { pmTaskRows, pmTasks } from './PmSchedule.transform';
import { CASES, FMEA_UPDATE_REVIEWS, RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';

export type MaintenanceTab = 'assets' | 'cond' | 'pm';

export type MaintenanceCopy = {
  title: string;
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

export const MAINTENANCE_TABS: { id: MaintenanceTab; label: string }[] = [
  { id: 'assets', label: 'Asset register' },
  { id: 'cond', label: 'Condition monitoring' },
  { id: 'pm', label: 'PM schedule' },
];

export function isMaintenanceTab(value: string | null): value is MaintenanceTab {
  return value === 'assets' || value === 'cond' || value === 'pm';
}

/** The three tab headlines (adjudication 5), each a count the tab's own
 *  transform derives: the register's items and critical tier, the monitoring
 *  points and the lowest health index, the open schedule rows. */
export function maintenanceTabHeadlines(state: SessionState): Record<MaintenanceTab, string> {
  const items = MAINTAINABLE_ITEMS;
  const critical = items.filter((item) => item.criticality === 'Critical').length;
  const points = conditionMonitoringPoints(state);
  const healthTable = tablesForMaintenanceTab('cond')[0];
  const lowestHealth = healthTable
    ? Math.min(...equipmentHealthMeters(healthTable).map((row) => row.healthPct))
    : null;
  const open = pmTaskRows(pmTasks(), state.cursor).length;
  return {
    assets: `${items.length} items · ${critical} critical`,
    cond: lowestHealth === null ? `${points.length} points` : `${points.length} points · lowest health ${lowestHealth}%`,
    pm: `${open} open · next 14 days`,
  };
}

export type WorkOrderTrigger = 'CBM' | 'PM' | 'Failure-finding' | 'Inspection';

export type WorkOrderRow = {
  id: string;
  wo: string;
  asset: string;
  description: string;
  trigger: WorkOrderTrigger;
  priority: string;
  status: string;
  estHours: string;
  assigned: string;
};

export type ConditionMonitoringPoint = {
  id: string;
  item: string;
  monitoringType: string;
  frequency: string;
  alertThreshold: string;
  statusChip?: string;
  trendReadout?: string;
  affectsLine?: string;
  caseId?: string;
};

/** A §M9 ¶6 — no shutdown calendar or duration table in the document set. */
// narration-ok: tooltip behind the [syn] chip — provenance, the traceability record
export const PM_SCHEDULE_SYN_TOOLTIP =
  'Nothing in the site document set provides this as a schedule — no shutdown calendar or duration table exists in the Functional Description or FMEA.';

export const WO_CMMS_PROVENANCE = 'CMMS not yet identified — WO data seeded [syn]';

/** A §M9 ¶4 — verbatim CM inventory; required no invented data. */
const CONDITION_MONITORING_POINTS: readonly ConditionMonitoringPoint[] = [
  {
    id: 'cm-1',
    item: 'BM01 trunnion bearing',
    monitoringType: 'Oil analysis (Fe, Cu, viscosity, water content)',
    frequency: 'Monthly',
    alertThreshold: 'Rising Fe/Cu particulates; water content > 0.1%',
  },
  {
    id: 'cm-2',
    item: 'BM01 trunnion bearing',
    monitoringType: 'Bearing temperature (TE-622-625)',
    frequency: 'Continuous (DCS)',
    alertThreshold: 'Bearing temperature warning and specification limit',
  },
  {
    id: 'cm-3',
    item: 'BM01 liner bolts',
    monitoringType: 'Vibration spectrum (accelerometer, if fitted)',
    frequency: 'Baseline at each liner change',
    alertThreshold: 'Impulsive spikes at mill rotation frequency',
  },
  {
    id: 'cm-4',
    item: 'BM01 motor stator',
    monitoringType: 'Thermography + MCSA (motor current signature)',
    frequency: 'Annual / semi-annual',
    alertThreshold: 'Winding temp +5°C above baseline; current imbalance >3% between phases',
  },
  {
    id: 'cm-5',
    item: 'Cyclone Feed Pump',
    monitoringType: 'Vibration route + quarterly impeller measurement',
    frequency: 'Monthly / quarterly',
    alertThreshold:
      'VSD% rising against discharge pressure PT — pump curve degrading vs new-pump baseline',
  },
  {
    id: 'cm-6',
    item: 'Zn Rougher Agitator gearbox',
    monitoringType: 'Oil analysis',
    frequency: 'Quarterly',
    alertThreshold: 'Rising Fe particles, viscosity change',
  },
  {
    id: 'cm-7',
    item: 'Belt Scale WIT-021',
    monitoringType: 'Calibration cross-check vs shift tonnage',
    frequency: 'Monthly',
    alertThreshold: 'Integrated tonnage diverging >3% from manual shift count over 30 days',
  },
];

export const CM_SERIES_BY_POINT: Record<string, string> = {
  'cm-1': 'rs-cm-oil-fe',
  'cm-2': 'rs-bm-brg-temp',
  'cm-3': 'rs-cm-liner-vib',
  'cm-4': 'rs-cm-stator-therm',
  'cm-5': 'rs-cyc-pump-vsd-6w',
  'cm-6': 'rs-cm-zn-gearbox-oil',
  'cm-7': 'rs-cm-wit-cal',
};

function shortSeriesTrend(seriesId: string, state: SessionState, unit: string): string | undefined {
  const view = seriesAt(seriesId, state);
  const numeric = view.points.filter((p) => p.value !== null);
  if (numeric.length < 2) return undefined;
  const start = numeric[0]?.value;
  const end = numeric[numeric.length - 1]?.value;
  if (start === undefined || end === undefined || start === null || end === null) return undefined;
  const delta = end - start;
  const sign = delta >= 0 ? '+' : '−';
  return `${start.toFixed(1)} → ${end.toFixed(1)} ${unit} (${sign}${Math.abs(delta).toFixed(1)})`;
}

export function maintenanceCopy(_plantName: string): MaintenanceCopy {
  return { title: 'Maintenance' };
}

function openWorkOrdersTable(): RoleTable | undefined {
  return ALL_TABLES.find(
    (table) =>
      table.roleId === 'maintenance' &&
      table.tabId === 'overview' &&
      table.caption.startsWith('Open work orders'),
  );
}

/** WO trigger taxonomy — [syn] per A §M9 ¶3; inferred from description text. */
export function inferWorkOrderTrigger(description: string): WorkOrderTrigger {
  const d = description.toLowerCase();
  if (
    d.includes('pm overdue') ||
    d.includes('oil service') ||
    d.includes('filter cloth') ||
    d.includes('overhaul')
  ) {
    return 'PM';
  }
  if (d.includes('temp') || d.includes('worn') || d.includes('apex')) {
    return 'CBM';
  }
  if (d.includes('rebuild')) {
    return 'Failure-finding';
  }
  if (d.includes('inspect')) {
    return 'Inspection';
  }
  return 'PM';
}

export function workOrderRows(): WorkOrderRow[] {
  const table = openWorkOrdersTable();
  if (!table) return [];
  return table.rows.map((row) => {
    const description = row.cells[2] ?? '';
    return {
      id: row.id,
      wo: row.cells[0] ?? '',
      asset: row.cells[1] ?? '',
      description,
      trigger: inferWorkOrderTrigger(description), // [syn] per A §M9 ¶3
      priority: row.cells[3] ?? '',
      status: row.cells[4] ?? '',
      estHours: row.cells[5] ?? '',
      assigned: row.cells[6] ?? '',
    };
  });
}

export function conditionMonitoringPoints(state: SessionState): readonly ConditionMonitoringPoint[] {
  const pumpTrend = longTrend('cyclone-pump-vsd', state);
  const bearingTrend = bm01BearingTrend(state);
  const bearingReading = readingByIdLookup('rd-bm-brg-temp');
  const warnHi = bearingReading?.band?.hi;
  const limitHi = bearingReading?.spec?.hi;
  const bmCase = CASES.find((c) => c.id === 'case-bm-brg');

  return CONDITION_MONITORING_POINTS.map((row) => {
    if (row.id === 'cm-2' && warnHi !== undefined && limitHi !== undefined) {
      // The limit comparison stands whatever the cursor; the trend itself reads
      // `unmeasured` before the post-restart samples can be fitted (#782).
      const latestValue = bearingTrend.currentValue;
      if (bearingTrend.state !== 'fitted') {
        return {
          ...row,
          alertThreshold: `${warnHi} °C warning · ${limitHi} °C limit`,
          statusChip: latestValue !== null && latestValue >= warnHi ? 'warn' : 'stable',
          trendReadout: bm01BearingTrendPhrase(bearingTrend),
          affectsLine: bmCase ? formatAffectsLine(bmCase, state) : undefined,
          caseId: 'case-bm-brg',
        };
      }
      const delta = bearingTrend.currentValue - bearingTrend.restartValue;
      const deltaSign = delta >= 0 ? '+' : '−';
      return {
        ...row,
        alertThreshold: `${warnHi} °C warning · ${limitHi} °C limit`,
        statusChip: bearingTrend.currentValue >= warnHi ? 'warn' : 'rising',
        trendReadout: `${bearingTrend.restartValue.toFixed(1)} → ${bearingTrend.currentValue.toFixed(1)} °C (${deltaSign}${Math.abs(delta).toFixed(1)} °C) · ${bm01BearingTrendPhrase(bearingTrend)}`,
        affectsLine: bmCase ? formatAffectsLine(bmCase, state) : undefined,
        caseId: 'case-bm-brg',
      };
    }
    if (row.id === 'cm-5' && pumpTrend) {
      const delta = pumpTrend.endValue - pumpTrend.startValue;
      return {
        ...row,
        statusChip: pumpTrend.trend === 'rising' ? 'rising' : pumpTrend.trend,
        trendReadout: `VSD ${pumpTrend.startValue.toFixed(0)} → ${pumpTrend.endValue.toFixed(0)} % (+${delta.toFixed(0)} pt)`,
        caseId: 'case-cyc-wear',
      };
    }
    const seriesId = CM_SERIES_BY_POINT[row.id];
    if (seriesId) {
      const unit =
        row.id === 'cm-1' || row.id === 'cm-6'
          ? 'ppm Fe'
          : row.id === 'cm-3'
            ? 'mm/s'
            : row.id === 'cm-4'
              ? '°C'
              : '%';
      const trendReadout = shortSeriesTrend(seriesId, state, unit);
      if (trendReadout) {
        return {
          ...row,
          statusChip: 'stable',
          trendReadout,
        };
      }
    }
    return row;
  });
}

export function tablesForMaintenanceTab(tab: MaintenanceTab): RoleTable[] {
  const roleId = 'maintenance';
  if (tab === 'cond') {
    return ALL_TABLES.filter(
      (table) =>
        table.roleId === roleId &&
        table.tabId === 'overview' &&
        table.caption.startsWith('Equipment health index'),
    );
  }
  return ALL_TABLES.filter(
    (table) =>
      table.roleId === roleId &&
      table.tabId === 'overview' &&
      table.caption.startsWith('Upcoming planned maintenance'),
  );
}

const MAINT_CASE_IDS = new Set(
  CASES.filter((row) => row.assignedTo === 'maintenance').map((row) => row.id),
);

/** Week-urgency proposed actions on maintenance-assigned cases — PM Schedule tab. */
export function pendingAdvisoryPmActions(): RecommendedAction[] {
  return RECOMMENDED_ACTIONS.filter(
    (action) =>
      action.status === 'proposed' &&
      action.urgency === 'week' &&
      MAINT_CASE_IDS.has(action.caseId),
  );
}

export type LowestHealthWorkRow = {
  asset: string;
  healthPct: number;
  wo: string;
  description: string;
  priority: string;
  status: string;
};

/** Two lowest health-index assets and their open work orders (B: Maintenance). */
export function lowestTwoOpenWork(): LowestHealthWorkRow[] {
  const healthTable = ALL_TABLES.find(
    (table) =>
      table.roleId === 'maintenance' &&
      table.tabId === 'overview' &&
      table.caption.startsWith('Equipment health index'),
  );
  if (!healthTable) return [];

  const ranked = healthTable.rows
    .map((row) => ({
      asset: row.cells[0] ?? '',
      healthPct: Number.parseFloat((row.cells[1] ?? '0').replace('%', '')),
    }))
    .filter((row) => Number.isFinite(row.healthPct))
    .sort((a, b) => a.healthPct - b.healthPct)
    .slice(0, 2);

  return ranked.flatMap(({ asset, healthPct }) => {
    const woRow = workOrderRows().find((row) => {
      const woAsset = row.asset.toLowerCase();
      const healthAsset = asset.toLowerCase();
      return healthAsset.includes(woAsset) || woAsset.includes(healthAsset.split('(')[0]?.trim() ?? healthAsset);
    });
    if (!woRow) return [];
    return [
      {
        asset,
        healthPct,
        wo: woRow.wo,
        description: woRow.description,
        priority: woRow.priority,
        status: woRow.status,
      },
    ];
  });
}

const MECH_ELEC_CATEGORIES = new Set(['mechanical-electrical', 'spans-both']);

export function pendingMechElecFmeaReviewCount(): number {
  return FMEA_UPDATE_REVIEWS.filter(
    (row) => row.reviewStatus === 'pending' && MECH_ELEC_CATEGORIES.has(row.causeCategory),
  ).length;
}

/** The flowsheet subprocesses each Capacity circuit covers — the join from a
 *  maintainable item's area to the circuit the engine names as the constraint.
 *  Paste & tails and water sit outside the Capacity model and join no circuit. */
const SUBPROCESSES_BY_CIRCUIT: Record<CapacityCircuitId, readonly string[]> = {
  crushing: ['sp-crush'],
  grinding: ['sp-grind'],
  'lead-flotation': ['sp-pb'],
  'zinc-flotation': ['sp-zn'],
  dewatering: ['sp-filter'],
};

export type PlantConstraintRead = {
  circuitId: CapacityCircuitId;
  /** `Grinding` — the Capacity engine's bottleneck circuit. */
  circuit: string;
  feedStph: number;
  ceilingStph: number;
  /** `71.7 stph · of the 79.2 stph grinding ceiling` (D27). */
  comparison: string;
  subprocessIds: readonly string[];
};

/** The header's constraint fact (polish.P21 adjudication 3, D27): the Capacity
 *  engine's bottleneck at the session cursor, the current feed against its
 *  maximum achievable — the same `evaluateCapacity` read through
 *  `capacityPlantState` that `/planning` and `/capacity` make. Never typed. */
export function plantConstraint(state: SessionState): PlantConstraintRead {
  const plantState = capacityPlantState(state);
  const read = evaluateCapacity({ grade: plantState.grade, seed: CAPACITY_SEED, plantState });
  const circuit = read.circuits.find((row) => row.id === read.bottleneck);
  if (!circuit) throw new Error(`plantConstraint: no circuit ${read.bottleneck}`);
  const circuitId = read.bottleneck as CapacityCircuitId;
  return {
    circuitId,
    circuit: circuit.name,
    feedStph: read.currentThroughputStph,
    ceilingStph: read.maxAchievableStph,
    comparison: `${read.currentThroughputStph.toFixed(1)} stph · of the ${read.maxAchievableStph.toFixed(1)} stph ${CEILING_LABEL_BY_CIRCUIT[circuitId]}`,
    subprocessIds: SUBPROCESSES_BY_CIRCUIT[circuitId] ?? [],
  };
}

export function parseHealthPct(value: string): number {
  return Number.parseInt(value.replace('%', ''), 10);
}

/** B: slide 11 — health index meter tone by band. */
export function healthMeterTone(pct: number): VizTone {
  if (pct >= 80) return 'ok';
  if (pct >= 65) return 'warn';
  return 'bad';
}

export type EquipmentHealthMeter = {
  id: string;
  asset: string;
  healthPct: number;
  healthLabel: string;
  tone: VizTone;
};

export function equipmentHealthMeters(table: RoleTable): EquipmentHealthMeter[] {
  return table.rows.map((row) => {
    const healthLabel = row.cells[1] ?? '0%';
    const healthPct = parseHealthPct(healthLabel);
    return {
      id: row.id,
      asset: row.cells[0] ?? '',
      healthPct,
      healthLabel,
      tone: healthMeterTone(healthPct),
    };
  });
}
